<script setup lang="ts">
import { computed, onUnmounted, ref, useTemplateRef, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import Loading from "@/components/Loading.vue";
import CaseFlex from "@/components/CaseFlex";
import CaseFixed from "@/components/CaseFixed";
import CaseMenuBar from "@/components/CaseMenuBar";
import CaseFindingLayout from "@/components/CaseFindingLayout";

import { useCaseStore } from "@/stores/case-store";
import { useMessageStore } from "@/stores/message-store";
import { useCaseFileStore } from "@/stores/case-file-store";
import { useMetadataStore } from "@/stores/metadata-store";
import { isItemBurned, isItemBurning } from "@/services/UtilitiesService";

import { useRootLayout } from "@/hooks/use-root-layout";
import { useCaseRouteParams } from "@/hooks/use-case-route-params";

const $route = useRoute();
const $router = useRouter();
const { layout } = useRootLayout();
const routeParams = useCaseRouteParams();

const loadingState = ref<"success" | "error" | "loading">();

const caseFlexRef = useTemplateRef<InstanceType<typeof CaseFlex>>("case-flex");

const isCaseRoute = computed(() => {
  if ($route.name == null) return false;
  return $route.name.toString().startsWith("case");
});

if (isCaseRoute.value) {
  loadingState.value = "loading";

  void useCaseStore()
    .onCaseNameChange()
    .then(() => {
      loadingState.value = "success";
    })
    .catch((r) => {
      loadingState.value = "error";
      console.error("Redirecting. Error occurred while entering case", r);

      const statusCode = (r as { status: number }).status;

      $router.push({
        name: "error",
        params: { brand: routeParams.value.brand, code: statusCode },
        replace: statusCode === 403,
      });
    });

  watch(
    [() => routeParams.value.casefile],
    async () => {
      if (routeParams.value.casefile) {
        const { brand, casename, casefile } = routeParams.value;

        useCaseFileStore().addIntoCache({
          fileReference: casefile,
          path: `/${brand}/${casename}/${casefile}`,
        });
      }

      await useCaseFileStore().onSelectedFileChange();
    },
    { immediate: true },
  );

  // Waits for this case to load: moving straight from another case mounts this page while the
  // metadata store still holds that case's files.
  watch(
    [() => useMetadataStore().filteredFiles, loadingState],
    ([filteredFiles]) => {
      const { brand, casename, casefile } = routeParams.value;
      if (loadingState.value !== "success" || filteredFiles.length !== 1 || casefile) return;

      $router.replace({
        name: "casefile",
        params: { brand, casename, casefile: filteredFiles[0].fileReference },
      });
    },
    { immediate: true },
  );
}

watch(
  () => useCaseFileStore().currentFile,
  (next, prev) => {
    if (prev == null || next == null) return;

    const isFileBurning = isItemBurning(next, false);

    // If the file is still burning
    // and the flag to optimistically share the update is still true
    if (isFileBurning && useCaseFileStore().cachedFilesData[next.fileReference]?.hasCommittedRedactions === true) {
      // Toggle to false. i.e. the file now has the `edxBurning` flag
      useCaseFileStore().setCommittedRedactionsState(next.fileReference, false);
    }

    // The file wasn't burning or is still burning if
    // - The updated  file         has  edxBurning
    // - The updated  file doesn't have edxBurned
    // - The previous file doesn't have edxBurning
    if (isFileBurning || !isItemBurned(next) || !isItemBurning(prev, false)) return;

    // We should re-select its metadata
    useCaseFileStore().onSelectedFileChange();
  },
);

onUnmounted(() => {
  useMessageStore().clearMessage();
  // Ensure we clear down the metadata in case of a redirect
  useCaseStore().onCaseNameChange();
});
</script>
<template>
  <Loading v-if="loadingState === 'loading'" id="case-loading" message="Loading" />
  <div v-else-if="loadingState === 'error'" class="tw-grid tw-size-full tw-place-items-center">
    <div class="tw-max-w-sm tw-text-pretty">Error occurred while entering case</div>
  </div>
  <!-- CaseFixed/CaseFlex must stay mounted and in the visible DOM at all times, including while
       tool view (CaseFindingLayout) is open — they host AgentAssistantChat's live MCP UI iframes
       (see McpUiCard.vue), and CaseMenuBar's own "View full case" exit button and layout header
       (teleported into the shared header via CaseMenuBar's <Teleport>). Browsers reload an
       <iframe>'s content whenever it is physically reparented/reinserted in the DOM, which is
       exactly what KeepAlive does to whichever branch it deactivates — previously
       CaseFindingLayout shared one KeepAlive with CaseFixed/CaseFlex and swapped between them via
       v-if/else-if, so opening tool view deactivated (and reparented) CaseFixed/CaseFlex's whole
       subtree, silently resetting the chat iframe. Rendering CaseFindingLayout as a separate,
       absolutely-positioned overlay instead means CaseFixed/CaseFlex are never touched by it. -->
  <div v-else class="tw-relative tw-size-full">
    <!-- https://github.com/vuejs/core/issues/5603 -->
    <CaseMenuBar :dockview-ref="caseFlexRef?.dockviewRef" />
    <KeepAlive>
      <CaseFixed v-if="layout === 'FIXED'" :is-case-route="isCaseRoute" />
      <CaseFlex v-else-if="layout === 'FLEX'" ref="case-flex" :is-case-route="isCaseRoute" />
    </KeepAlive>
    <!-- CaseFindingLayout is a lazily-loaded Mosaic component that renders nothing until its chunk
         (which pulls in the document/transcript viewers) has downloaded, so the overlay and a spinner
         are shown straight away and the layout paints over them once it's ready. The spinner is
         isolated so its high z-index can't bleed through the loaded layout. -->
    <div v-if="useCaseStore().toolModeFindingReference != null" class="tw-absolute tw-inset-0 tw-z-10 tw-bg-white">
      <div class="tw-absolute tw-inset-0 tw-isolate">
        <Loading id="tool-mode-layout" message="Loading..." />
      </div>
      <CaseFindingLayout :is-case-route="isCaseRoute" class="tw-relative tw-bg-white" />
    </div>
  </div>
</template>
